import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';
import type { UserPermissions } from '../../hooks/useUserPermissions';

/**
 * Staff who have no business of their own and create passes in whichever business they pick as destination: the
 * tenant Front Desk and the estate group roles, and the Xhinola admin (`xhinola_admin` is not a matrix role, so
 * it was told "Business information not available", SAM-1311).
 */
export const picksPassBusiness = (p: Pick<UserPermissions, 'isTenantFrontDesk' | 'isEstateGroupAdmin' | 'isEstateGroupFrontDesk' | 'isXhinolaAdmin'>): boolean =>
  worksAsFrontDesk(p) || p.isXhinolaAdmin;

/**
 * Which business a pass is created for. A business user has one (`selectedBusinessId`), so that is what
 * was always sent. The tenant Front Desk has none: it creates passes in ANY business of the tenant, so the
 * business it picked as the destination is the pass's business. Everyone else keeps the old value.
 */
export const resolveInvitationBusinessId = (opts: {
  isTenantFrontDesk: boolean;
  destination?: { id?: string | number; isBusiness?: boolean } | null;
  selectedBusinessId: string | null;
}): string | null =>
  opts.isTenantFrontDesk && opts.destination?.isBusiness === true && opts.destination.id
    ? String(opts.destination.id)
    : opts.selectedBusinessId;

/**
 * What the AccessForm does when the kind is «business», given the destination already picked and the
 * business it can default to (the session's own, or the one looked up for it).
 * - a business destination already picked is kept, always: for the tenant Front Desk, who has no business of
 *   its own, `currentBusiness` is empty until the picked business is looked up, and resetting then wiped the
 *   destination the user had just chosen («How long?» stayed on «Please select a destination first»);
 * - otherwise default to the current business, or reset when there is none.
 */
export const businessKindAction = (opts: {
  isCurrentlyBusiness: boolean;
  hasCurrentBusiness: boolean;
}): 'keep' | 'use-current' | 'reset' => {
  if (opts.isCurrentlyBusiness) return 'keep';
  return opts.hasCurrentBusiness ? 'use-current' : 'reset';
};

/**
 * The business a confirmed pass or invitation is created for, and whether there is enough to create it.
 * - business pass (`business_access`): the business picked as destination, else the context business;
 * - residential invitation: the context business, which the tenant Front Desk does not have (null; the
 *   estate is what scopes it) and must not be blocked for.
 * Everyone else keeps the old rule: no context business, no pass.
 */
export const resolvePassBusiness = (opts: {
  endpointType: string;
  destination?: { id?: string | number; isBusiness?: boolean } | null;
  contextBusinessId: string | null;
  isTenantFrontDesk: boolean;
}): { businessId: string | null; ok: boolean } => {
  const picked =
    opts.endpointType === 'business_access' && opts.destination?.isBusiness === true && opts.destination.id
      ? String(opts.destination.id)
      : null;
  const businessId = picked ?? opts.contextBusinessId;
  if (!opts.isTenantFrontDesk) return { businessId, ok: !!opts.contextBusinessId };
  return { businessId, ok: opts.endpointType !== 'business_access' || !!businessId };
};

const CLIENT_BOOKING_ACCESS_TYPE_ID = 2;

/**
 * `business_access_type_id` of a pass; `undefined` means the field is not sent. Everyone else keeps the old
 * rule (the picked type, else 2). `business_access_type` is per tenant (its ids differ), so the tenant Front
 * Desk never falls back to a fixed id: it sends the type it picked, whichever it is (the backend checks it
 * belongs to the tenant), and with none picked it sends nothing and the backend puts Guest (SAM-769).
 */
export const resolveBusinessAccessTypeId = (opts: {
  picked?: { id?: number; label?: unknown } | null;
  isTenantFrontDesk: boolean;
}): number | undefined => {
  const id = opts.picked?.id || undefined;
  return opts.isTenantFrontDesk ? id : id ?? CLIENT_BOOKING_ACCESS_TYPE_ID;
};

/**
 * The business a Client Booking (product or service, paid) is sold for: the business picked as destination when
 * there is one, else the context business. The tenant Front Desk has no context business, so everything the
 * flow needs from it (the «no business» guard, the names sent to the payment gateway, the confirmation card)
 * comes from the destination (SAM-769).
 */
export const resolveBookingBusiness = (opts: {
  destination?: { id?: string | number; label?: unknown; isBusiness?: boolean } | null;
  contextBusiness?: { id?: string | null; name?: string | null } | null;
}): { id: string | null; name: string | undefined } => {
  const d = opts.destination;
  if (d?.isBusiness === true && d.id) {
    return { id: String(d.id), name: typeof d.label === 'string' && d.label ? d.label : opts.contextBusiness?.name || undefined };
  }
  return { id: opts.contextBusiness?.id ?? null, name: opts.contextBusiness?.name || undefined };
};

/**
 * Who gets every pass kind in New pass (Residential and Business) instead of the kinds their business_* groups
 * allow: admins, the tenant Front Desk and the estate group roles, whose groups (estate_group_*) carry no
 * business_* flag at all, so they saw no kind and no destination. The roles come from the token's groups; the
 * server scopes what each may pick and create.
 */
export const seesAllPassKinds = (p: {
  isAdmin: boolean;
  isAllInvitationManager: boolean;
  isEstateGroupAdmin?: boolean;
  isEstateGroupFrontDesk?: boolean;
}): boolean => p.isAdmin || p.isAllInvitationManager || Boolean(p.isEstateGroupAdmin) || Boolean(p.isEstateGroupFrontDesk);
